import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { SeoAudit } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useSeoAudits } from '../hooks/use-seo-audits';
import { useRunSeoAudit } from '../hooks/use-seo-mutations';

const SHOWN = 5;

function AuditRow({ audit }: { audit: SeoAudit }) {
  const { t } = useTranslation('seo');
  const format = useFormat();
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 border-b py-2 text-sm last:border-0">
      <div className="flex flex-col gap-0.5">
        <span title={format.utc(audit.startedAt)}>{format.relative(audit.startedAt)}</span>
        <span className="text-muted-foreground text-xs">
          {audit.status === 'failed'
            ? (audit.error ?? t('audits.failed'))
            : t('audits.checked', { count: audit.entitiesChecked })}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={audit.status === 'failed' ? 'destructive' : 'outline'}>
          {t(`auditStatus.${audit.status}`)}
        </Badge>
        {audit.status === 'completed' ? (
          <span className="text-muted-foreground text-xs tabular-nums">
            {t('audits.counts', {
              error: audit.counts.error,
              warn: audit.counts.warn,
              info: audit.counts.info,
            })}
          </span>
        ) : null}
      </div>
    </li>
  );
}

/** Recent audit runs and the manual trigger; the worker runs it, like the nightly run. */
export function SeoAuditsCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('seo');
  const query = useSeoAudits(storeId);
  const run = useRunSeoAudit(storeId);
  const audits = query.data?.slice(0, SHOWN) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">{t('audits.title')}</CardTitle>
        <CardDescription>{t('audits.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Can permission="content.write">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={run.isPending}
              onClick={() => run.mutate()}
            >
              <Play aria-hidden />
              {run.isPending ? t('audits.queueing') : t('audits.run')}
            </Button>
            {run.isSuccess ? (
              <p role="status" className="text-muted-foreground text-xs">
                {t('audits.queued')}
              </p>
            ) : null}
          </div>
        </Can>
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
        ) : query.isPending ? (
          <Skeleton className="h-24" />
        ) : audits.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('audits.none')}</p>
        ) : (
          <ul aria-label={t('audits.recent')}>
            {audits.map((audit) => (
              <AuditRow key={audit.id} audit={audit} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
